import { useEffect, useRef } from 'react';
import { useStore } from 'react-admin';
import { AutoSaveStoredData } from 'src/constants/autoSave';

/**
 * Hook that clears auto-saved data when component unmounts
 * This prevents draft data from persisting when user deliberately navigates away
 *
 * Use `markAsSubmitting()` before successful form submission to prevent
 * the auto-save data from being cleared during navigation to success page.
 *
 * @param { string } storageKey - The key used for storing the auto-save data
 * @returns Object with markAsSubmitting function
 *
 * @example
 * ```tsx
 * const MyForm = () => {
 *   const { markAsSubmitting } = useClearAutoSaveOnUnmount('my-form-key');
 *
 *   const onSuccess = () => {
 *     markAsSubmitting(); // Prevent clearing on success navigation
 *     clearAutoSave(); // Manually clear after marking
 *   };
 *
 *   return <Form onSuccess={onSuccess}>...</Form>;
 * };
 * ```
 */
export const useClearAutoSaveOnUnmount = (storageKey: string) => {
    const [, setStoredData] = useStore<AutoSaveStoredData | undefined>(storageKey, undefined);
    const isSubmittingRef = useRef(false);

    // Mark as submitting to prevent clearing on successful submit navigation
    const markAsSubmitting = () => {
        isSubmittingRef.current = true;
    };

    // Clear on unmount (unless it was a successful submit)
    useEffect(() => {
        return () => {
            // Only clear if we have a valid storage key AND this was NOT a successful form submission
            if (storageKey && !isSubmittingRef.current) {
                setStoredData(undefined);
            }
        };
    }, [storageKey, setStoredData]);

    return { markAsSubmitting };
};

export default useClearAutoSaveOnUnmount;
